import { computed, reactive, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/app';
import {
  getAppLanguageLocales,
  mapAppLanguages,
} from '@/helpers/ConceptHelper';

/**
 * App languages from app.languages concepts (database order).
 * Use across the app for language tabs, switchers, and locale-keyed forms.
 */
export function useAppLanguages() {
  const appStore = useAppStore();
  const { concepts } = storeToRefs(appStore);

  const languageConcepts = computed(() => concepts.value?.['app.languages'] ?? null);

  const languages = computed(() => mapAppLanguages(languageConcepts.value));

  const locales = computed(() => getAppLanguageLocales(languageConcepts.value));

  const defaultLocale = computed(() => locales.value[0] ?? '');

  const resolveStoredLocale = (stored?: string | null) => {
    const normalized = (stored || '').toLowerCase();
    if (!normalized) {
      return locales.value[0] ?? '';
    }
    if (locales.value.includes(normalized)) {
      return normalized;
    }
    const matched = languages.value.find((lang) => {
      const shortName = (lang.shortName || '').toLowerCase();
      const key = String(lang.key || '').toLowerCase();
      return shortName === normalized || key === normalized;
    });
    if (matched?.shortName) {
      return matched.shortName.toLowerCase();
    }
    return locales.value[0] ?? '';
  };

  return {
    languages,
    locales,
    defaultLocale,
    languageConcepts,
    resolveStoredLocale,
  };
}

/**
 * Reactive locale-keyed map synced with app.languages from the database.
 */
export function createLocaleTranslationMap<T extends object>(
  factory: (locale: string) => T,
) {
  const { locales } = useAppLanguages();
  const map = reactive<Record<string, T>>({});

  watch(
    locales,
    (localeList) => {
      if (!localeList.length) {
        return;
      }
      for (const locale of localeList) {
        if (!(locale in map)) {
          map[locale] = factory(locale);
        }
      }
    },
    { immediate: true },
  );

  return map;
}

/** Ref-based locale map for components that use `.value` (e.g. useFormValidation). */
export function useLocaleKeyedRef<T extends object>(
  factory: (locale: string) => T,
): Ref<Record<string, T>> {
  const { locales } = useAppLanguages();
  const map = ref<Record<string, T>>({});

  watch(
    locales,
    (localeList) => {
      if (!localeList.length) {
        return;
      }
      const next = { ...map.value };
      for (const locale of localeList) {
        if (!(locale in next)) {
          next[locale] = factory(locale);
        }
      }
      map.value = next;
    },
    { immediate: true },
  );

  return map;
}

type LocaleDescriptionMap = Record<string, { description?: string } | undefined>;

function snapshotLocaleDescriptions(map: LocaleDescriptionMap): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const locale of Object.keys(map || {})) {
    snapshot[String(locale).toLowerCase()] = map[locale]?.description ?? "";
  }
  return snapshot;
}

function isLocaleDescriptionMapDirty(
  current: LocaleDescriptionMap,
  original: Record<string, string>,
): boolean {
  const locales = new Set([
    ...Object.keys(current || {}).map((locale) => String(locale).toLowerCase()),
    ...Object.keys(original || {}),
  ]);
  for (const locale of locales) {
    const currentValue = current?.[locale]?.description ?? current?.[locale.toUpperCase()]?.description ?? "";
    if (currentValue !== (original?.[locale] ?? "")) {
      return true;
    }
  }
  return false;
}

/** Tracks whether a locale-keyed HTML form has unsaved changes. */
export function useLocaleFormDirty(content: Ref<LocaleDescriptionMap>) {
  const original = ref(snapshotLocaleDescriptions(content.value));
  const isDirty = ref(false);

  const refreshDirty = () => {
    isDirty.value = isLocaleDescriptionMapDirty(content.value, original.value);
  };

  watch(content, refreshDirty, { deep: true });

  const setDescription = (locale: string, value: string) => {
    const key = String(locale || "").toLowerCase();
    const current = content.value[key] || content.value[locale];
    content.value = {
      ...content.value,
      [key]: {
        ...(current || {}),
        description: value ?? "",
      },
    };
  };

  const getDescription = (locale: string) => {
    const key = String(locale || "").toLowerCase();
    return content.value[key]?.description ?? content.value[locale]?.description ?? "";
  };

  const markSaved = () => {
    original.value = snapshotLocaleDescriptions(content.value);
    isDirty.value = false;
  };

  return { isDirty, setDescription, getDescription, markSaved };
}
